/**
 * Storage utilities for auth
 */
import { secureStorage } from '../../api/Services/SecureStorage';

const LOGIN_OBJ_KEY = 'loginObj';
const LOGIN_STATUS_KEY = 'loginstatus';

/**
 * Store user session data after successful login/signup
 * @param {Object} userData - User data from API
 */
export function storeUserSession(userData) {
  const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ');
  const modifiedData = {
    ...userData,
    code: userData.patientCode,
    sessionid: userData.sessionId,
    mobile: userData.mobileNumber,
    name: fullName || userData.name || '',
  };
  delete modifiedData.patientCode;
  delete modifiedData.mobileNumber
  delete modifiedData.sessionId;
  
  secureStorage.setItem('token', userData.authToken);
  localStorage.setItem(LOGIN_STATUS_KEY, 'true');
  localStorage.setItem(LOGIN_OBJ_KEY, JSON.stringify(modifiedData));
  secureStorage.setItem('patientData', modifiedData);
  secureStorage.setItem('patientSession', modifiedData);
  window.dispatchEvent(new CustomEvent('loginComplete', { detail: { loginData: modifiedData } }));
  return modifiedData;
}

/**
 * Get stored user data
 * @returns {Object|null} User data or null
 */
export function getUserData() {
  try {
    const data = localStorage.getItem(LOGIN_OBJ_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

/**
 * Check if user is logged in
 * @returns {boolean}
 */
export function isLoggedIn() {
  return secureStorage.getItem('patientData')?.code;
}

/**
 * Clear user session data
 */
export function clearUserSession() {
  localStorage.removeItem(LOGIN_OBJ_KEY);
  localStorage.removeItem(LOGIN_STATUS_KEY);
  secureStorage.removeItem('token');
  window.dispatchEvent(new CustomEvent('logoutComplete'));
}

/**
 * Get redirect path after login.
 * Priority: explicit redirect set → current page → home
 * @returns {string} Redirect path with query params
 */
export function getRedirectPath() {
  const redirectToCaresathi = localStorage.getItem('redirectToCaresathi');

  if (redirectToCaresathi === 'true') {
    localStorage.removeItem('redirectToCaresathi');
    return '/caresathi';
  }

  // Check sessionStorage first (for in-app redirects like membership)
  let redirectPath = sessionStorage.getItem('redirectAfterLogin');

  // Fallback to localStorage
  if (!redirectPath) {
    redirectPath = localStorage.getItem('redirectAfterLogin');
  }

  if (redirectPath) {
    // Clear both storages
    sessionStorage.removeItem('redirectAfterLogin');
    localStorage.removeItem('redirectAfterLogin');
    return redirectPath;
  }

  // Default: stay on whichever page the user was on when they logged in.
  // Exclude auth-only pages that should redirect to home after login.
  const currentPath = window.location.pathname + window.location.search;
  const authOnlyPages = ['/login', '/forgot-password-reset'];
  if (currentPath && !authOnlyPages.includes(window.location.pathname)) {
    return currentPath;
  }

  return '/';
}

/**
 * Set redirect path for after login
 * @param {string} path - Path to redirect to (can include query params)
 * @param {Object} options - Additional options
 * @param {boolean} options.useSession - Use sessionStorage instead of localStorage (default: true)
 */
export function setRedirectPath(path, options = { useSession: true }) {
  if (options.useSession) {
    sessionStorage.setItem('redirectAfterLogin', path);
  } else {
    localStorage.setItem('redirectAfterLogin', path);
  }
}

/**
 * Build redirect path with query params
 * @param {string} basePath - Base path (e.g., '/membership/form')
 * @param {Object} params - Query parameters object
 * @returns {string} Full path with query string
 */
export function buildRedirectPath(basePath, params = {}) {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, value);
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

/**
 * Store redirect info for membership purchase flow
 * @param {Object} options - Redirect options
 * @param {string} options.plan - Plan type ('kavach' or 'kavach-plus')
 * @param {string} options.ref - Referral code (optional)
 * @param {string} options.returnPath - Custom return path (optional)
 */
export function setMembershipRedirect({ plan, ref, returnPath }) {
  const params = { plan };
  if (ref) params.ref = ref;
  
  const path = buildRedirectPath(returnPath || '/membership/form', params);
  setRedirectPath(path, { useSession: true });
}

/**
 * Generic function to set post-login redirect with params
 * Scalable for any page that needs login-protected access
 * @param {string} basePath - The base path to redirect to
 * @param {Object} params - Query parameters to include
 */
export function setProtectedPageRedirect(basePath, params = {}) {
  const path = buildRedirectPath(basePath, params);
  setRedirectPath(path, { useSession: true });
}
